import { useEffect, useRef, useState } from 'react';
import axios from '../utils/axios';
import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LIST_PAGE_SIZE,
} from '../constants';
import type {
  PagedResult,
  SessionOrganization,
  UserRecord,
  UserTotalsParams,
} from '../types/index';
import { throwIfLegacyResultError } from '../utils/requestErrors';

type UseUsersParams = UserTotalsParams & {
  enabled?: boolean;
};

type UseUsersResult = {
  data: UserRecord[];
  error: unknown;
  isLoading: boolean;
  refetch: () => Promise<void>;
  total: number;
};

export async function getUsers({
  organizationId,
  page = DEFAULT_LIST_PAGE,
  pageSize = 50,
}: UserTotalsParams = {}): Promise<UserRecord[]> {
  const response = await axios.get('/user', {
    params: { organizationId, page, pageSize },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getUsersTotals({
  organizationId,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  search = null,
  sortField = null,
  sortAscending = null,
}: UserTotalsParams = {}): Promise<PagedResult<UserRecord>> {
  const response = await axios.get('/user/totals', {
    params: { organizationId, page, pageSize, search, sortField, sortAscending },
  });

  return {
    page: Number(response.data?.page ?? page),
    pageSize: Number(response.data?.pageSize ?? pageSize),
    total: Number(response.data?.total ?? 0),
    data: Array.isArray(response.data?.data) ? response.data.data : [],
  };
}

export async function getUserRoles(
  organizationId: SessionOrganization['id'],
): Promise<string[]> {
  const response = await axios.get('/user/roles', {
    params: { organizationId },
  });

  if (!Array.isArray(response.data)) {
    return [];
  }

  return response.data
    .filter((item) => item?.allOrganizations !== true)
    .map((item) => String(item?.roleName || '').trim())
    .filter(Boolean);
}

export async function getInternalUserRolesTotals({
  organizationId = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  search = null,
}: {
  organizationId?: number | string | null;
  page?: number;
  pageSize?: number;
  search?: string | null;
} = {}): Promise<PagedResult<any>> {
  const response = await axios.get('/user/internal-roles', {
    params: { organizationId, page, pageSize, search },
  });
  const data = throwIfLegacyResultError(response.data, 'Internal roles could not be loaded.');

  return {
    page: Number(data?.page ?? page),
    pageSize: Number(data?.pageSize ?? pageSize),
    total: Number(data?.total ?? 0),
    data: Array.isArray(data?.data) ? data.data : [],
  };
}

export async function getCurrentUserInternalRole(): Promise<string> {
  const response = await axios.get('/user/internal-roles/current-user');
  const data = throwIfLegacyResultError(response.data, 'Internal role could not be loaded.');
  if (typeof data === 'string') return data.trim();
  return String(
    data?.allOrganizationsRoleName ||
    data?.AllOrganizationsRoleName ||
    data?.roleName ||
    data?.RoleName ||
    data?.internalRole ||
    ''
  ).trim();
}

export async function saveInternalUserRole(payload: {
  userId: number;
  roleName: string;
}): Promise<unknown> {
  const response = await axios.post('/user/internal-roles', payload);
  return throwIfLegacyResultError(response.data, 'Could not save the internal user role.');
}

export async function revokeInternalUserRole(payload: {
  userId: number;
  roleName?: string;
}): Promise<unknown> {
  const response = await axios.delete('/user/internal-roles', { data: payload });
  return throwIfLegacyResultError(response.data, 'Could not revoke the internal user role.');
}

export async function getAdminUsersReport(): Promise<any[]> {
  const response = await axios.get('/reports/admin-users');
  const data = throwIfLegacyResultError(response.data, 'Could not retrieve admin users report.');
  return Array.isArray(data) ? data : [];
}

export async function addUser(payload: unknown): Promise<unknown> {
  const response = await axios.post('/user/add', payload);
  return response.data;
}

export async function denyUserRequestToAccessOrganization(payload: {
  email: string;
  organizationId: number | string;
}): Promise<unknown> {
  const response = await axios.post('/user/deny', payload);
  return response.data;
}

export async function editUser(payload: unknown): Promise<unknown> {
  const response = await axios.post('/user/edit', payload);
  return response.data;
}

export async function removeUser(payload: unknown): Promise<unknown> {
  const response = await axios.post('/user/remove', payload);
  return response.data;
}

export async function searchUsersByEmail({
  email,
  sortField = null,
  sortAscending = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = 50,
}: {
  email: string;
  sortField?: string | null;
  sortAscending?: boolean | null;
  page?: number;
  pageSize?: number;
}): Promise<UserRecord[]> {
  const response = await axios.get('/user/search/email', {
    params: { email, sortField, sortAscending, page, pageSize },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getAdminUsers({
  email = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = 50,
}: {
  email?: string | null;
  page?: number;
  pageSize?: number;
} = {}): Promise<any[]> {
  const response = await axios.get('/user/admin/list', {
    params: { email, page, pageSize },
  });
  const data = throwIfLegacyResultError(response.data, 'Could not search users.');
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.Items)) return data.Items;
  return [];
}

export async function promoteSuperUser(userId: number): Promise<any> {
  const response = await axios.post('/user/promote-super-user', { userId });
  return throwIfLegacyResultError(response.data, 'Could not promote user to super user.');
}

export async function demoteSuperUser(userId: number): Promise<any> {
  const response = await axios.post('/user/demote-super-user', { userId });
  return throwIfLegacyResultError(response.data, 'Could not demote user from super user.');
}

export async function manuallyVerifyUsers(payload: { userIds: number[] }): Promise<any> {
  const response = await axios.post('/user/verify/email/batch', payload);
  return response.data;
}

export async function sendUserPasswordReset({ userId }: { userId: number }): Promise<any> {
  const response = await axios.post(`/user/${userId}/password-reset`);
  return response.data;
}

export async function getUserAccessMarkets({
  organizationId,
  facilityIds = [],
  brandIds = [],
}: {
  organizationId?: number | string | null;
  facilityIds?: Array<number | string>;
  brandIds?: Array<number | string>;
}): Promise<any[]> {
  const response = await axios.get('/user/access-options/markets', {
    params: {
      organizationId,
      facilityIds: facilityIds.length ? facilityIds.join(',') : undefined,
      brandIds: brandIds.length ? brandIds.join(',') : undefined,
    },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getUserAccessFacilities({
  organizationId,
  marketIds = [],
  brandIds = [],
}: {
  organizationId?: number | string | null;
  marketIds?: Array<number | string>;
  brandIds?: Array<number | string>;
}): Promise<any[]> {
  const response = await axios.get('/user/access-options/facilities', {
    params: {
      organizationId,
      marketIds: marketIds.length ? marketIds.join(',') : undefined,
      brandIds: brandIds.length ? brandIds.join(',') : undefined,
    },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getUserAccessBrands({
  organizationId,
  marketIds = [],
  facilityIds = [],
}: {
  organizationId?: number | string | null;
  marketIds?: Array<number | string>;
  facilityIds?: Array<number | string>;
}): Promise<any[]> {
  const response = await axios.get('/user/access-options/brands', {
    params: {
      organizationId,
      marketIds: marketIds.length ? marketIds.join(',') : undefined,
      facilityIds: facilityIds.length ? facilityIds.join(',') : undefined,
    },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export default function useUsers({
  organizationId,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  search = null,
  sortField = null,
  sortAscending = null,
  enabled = true,
}: UseUsersParams = {}): UseUsersResult {
  const [data, setData] = useState<UserRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<unknown>(null);
  const requestIdRef = useRef(0);
  const [dataOrganizationId, setDataOrganizationId] = useState(organizationId);

  if (dataOrganizationId !== organizationId) {
    setDataOrganizationId(organizationId);
    setData([]);
    setTotal(0);
    setError(null);
    setIsLoading(Boolean(enabled && organizationId));
  }

  const refetch = async () => {
    const requestId = ++requestIdRef.current;

    if (!enabled || !organizationId) {
      setData([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await getUsersTotals({ organizationId, page, pageSize, search, sortField, sortAscending });
      if (requestId !== requestIdRef.current) return;
      setData(results.data);
      setTotal(results.total);
    } catch (nextError) {
      if (requestId !== requestIdRef.current) return;
      setError(nextError);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    refetch().catch(() => null);
    return () => {
      requestIdRef.current += 1;
    };
  }, [enabled, organizationId, page, pageSize, search, sortAscending, sortField]);

  return {
    data,
    error,
    isLoading,
    refetch,
    total,
  };
}
